import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { createAbTest as createAbTestRequest } from '../../../api/abTests.js'
import { listElements } from '../../../api/elements.js'
import { listGroupConfigSegments, listGroupConfigs } from '../../../api/groupConfigs.js'
import { listSegmentations } from '../../../api/segmentations.js'
import { actions } from '../../../local/remoteConfigStore.js'
import { useRemoteConfigAppState, useRemoteConfigMemo } from '../../../local/useRemoteConfigStore.js'
import { METRIC_DAYS_OPTIONS, TARGET_METRIC_OPTIONS } from './abTestMetricOptions.js'

export default function AbTestCreatePage() {
  const { applicationId } = useOutletContext()
  const storeActions = useRemoteConfigMemo(() => actions(), [])
  const appState = useRemoteConfigAppState(applicationId)
  const navigate = useNavigate()
  const [groupConfigs, setGroupConfigs] = useState([])
  const [segmentsByGroupConfigId, setSegmentsByGroupConfigId] = useState({})
  const [segmentationsById, setSegmentationsById] = useState({})
  const [elements, setElements] = useState([])

  const [values, setValues] = useState({
    groupConfigId: '',
    groupSegmentId: '',
    name: '',
    audience: '',
    targetMetric: '',
    metricDays: '',
    targetUserCount: '',
    isOnlyNewUser: false,
    automaticDistributionStop: false,
    releaseAllVersions: false,
    testFinishDate: '',
  })
  const [variants, setVariants] = useState([
    { name: 'Default', trafficPercent: 0, isDefault: true, elementId: '' },
    { name: 'A', trafficPercent: 100, isDefault: false, elementId: '' },
  ])
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    Promise.all([listGroupConfigs(applicationId), listSegmentations(applicationId), listElements(applicationId)])
      .then(([groupConfigsData, segmentationsData, elementsData]) => {
        if (cancelled) return
        setGroupConfigs(groupConfigsData)
        setElements(elementsData)
        storeActions.hydrateElements(applicationId, elementsData)
        const segmentationsMap = {}
        for (const segmentation of segmentationsData) {
          if (!segmentation?.id) continue
          segmentationsMap[segmentation.id] = segmentation
        }
        setSegmentationsById(segmentationsMap)
      })
      .catch((loadError) => {
        if (cancelled) return
        setError(loadError)
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, storeActions])

  useEffect(() => {
    const groupConfigId = values.groupConfigId
    if (!groupConfigId) return
    let cancelled = false
    listGroupConfigSegments(applicationId, groupConfigId)
      .then((segmentsData) => {
        if (cancelled) return
        setSegmentsByGroupConfigId((prev) => ({
          ...prev,
          [groupConfigId]: segmentsData,
        }))
      })
      .catch((loadError) => {
        if (cancelled) return
        setError(loadError)
      })
    return () => {
      cancelled = true
    }
  }, [applicationId, values.groupConfigId])

  const availableSegments = useMemo(() => {
    if (!values.groupConfigId) return []
    const normalizedGroupConfigId = String(values.groupConfigId || '').trim()
    const fromApi = segmentsByGroupConfigId[normalizedGroupConfigId] || []

    const draftVersions = Object.values(appState?.groupConfigVersions || {})
      .filter(
        (version) =>
          String(version?.groupConfigId || '').trim() === normalizedGroupConfigId &&
          String(version?.status || '').trim().toLowerCase() === 'draft',
      )
      .sort((a, b) => String(b?.createdAt || '').localeCompare(String(a?.createdAt || '')))
    const draftVersionId = String(draftVersions[0]?.id || '').trim()

    const fromDraft = draftVersionId
      ? Object.values(appState?.groupSegments || {}).filter(
          (segment) => String(segment?.groupConfigVersionId || '').trim() === draftVersionId,
        )
      : []

    const mergedById = new Map()
    for (const segment of fromApi) {
      const segmentId = String(segment?.id || '').trim()
      if (!segmentId) continue
      mergedById.set(segmentId, segment)
    }
    for (const segment of fromDraft) {
      const segmentId = String(segment?.id || '').trim()
      if (!segmentId || mergedById.has(segmentId)) continue
      mergedById.set(segmentId, segment)
    }

    return Array.from(mergedById.values()).sort(
      (a, b) => Number(a?.priority || 0) - Number(b?.priority || 0),
    )
  }, [appState, segmentsByGroupConfigId, values.groupConfigId])

  const groupSegment = useMemo(
    () => availableSegments.find((segment) => String(segment.id) === String(values.groupSegmentId)) || null,
    [availableSegments, values.groupSegmentId],
  )
  const segmentation = groupSegment ? segmentationsById[groupSegment.segmentationId] : null

  const publishedElements = useMemo(() => {
    const list = elements.filter((el) => {
      if (el.status !== 'published') return false
      if (!values.groupConfigId) return true
      return String(el.groupConfigId || '') === String(values.groupConfigId)
    })
    list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    return list
  }, [elements, values.groupConfigId])

  function onChange(field) {
    return (e) => {
      const isCheckbox = e.target.type === 'checkbox'
      const next = isCheckbox ? e.target.checked : e.target.value
      setValues((prev) => {
        if (field === 'groupConfigId') {
          return { ...prev, groupConfigId: next, groupSegmentId: '' }
        }
        if (field === 'automaticDistributionStop' && !next) {
          return { ...prev, automaticDistributionStop: next, testFinishDate: '' }
        }
        return { ...prev, [field]: next }
      })
    }
  }

  function updateVariant(variantIndex, patch) {
    setVariants((prev) =>
      prev.map((item, idx) => (idx === variantIndex ? { ...item, ...patch } : item)),
    )
  }

  function addVariant() {
    const nonDefault = variants.filter((v) => !v.isDefault)
    const newPct = nonDefault.length > 0 ? Math.floor(100 / (nonDefault.length + 1)) : 100
    const remainder = 100 - newPct * (nonDefault.length + 1)
    setVariants((prev) => {
      const next = prev.map((v) => {
        if (v.isDefault) return v
        return { ...v, trafficPercent: newPct }
      })
      const letters = 'ABCDEFGH'
      const used = new Set(next.map((v) => v.name?.trim().toUpperCase()))
      let name = 'B'
      for (const l of letters) {
        if (!used.has(l)) {
          name = l
          break
        }
      }
      next.push({
        name,
        trafficPercent: newPct + remainder,
        isDefault: false,
        elementId: '',
      })
      return next
    })
  }

  function removeVariant(variantIndex) {
    const v = variants[variantIndex]
    if (v?.isDefault) return
    const nonDefault = variants.filter((x) => !x.isDefault)
    if (nonDefault.length <= 1) return
    const remainingCount = nonDefault.length - 1
    const newPct = Math.floor(100 / remainingCount)
    const remainder = 100 - newPct * remainingCount
    setVariants((prev) => {
      const filtered = prev.filter((_, idx) => idx !== variantIndex)
      let idx = 0
      return filtered.map((x) => {
        if (x.isDefault) return x
        idx += 1
        return { ...x, trafficPercent: idx === 1 ? newPct + remainder : newPct }
      })
    })
  }

  function updateVariantElement(variantIndex, elementId) {
    setVariants((prev) =>
      prev.map((item, idx) => (idx === variantIndex ? { ...item, elementId } : item)),
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    const name = String(values.name || '').trim()
    if (!name) {
      setError(new Error('Name is required'))
      return
    }
    if (!values.groupConfigId) {
      setError(new Error('Group config is required'))
      return
    }
    if (!values.groupSegmentId) {
      setError(new Error('Segment is required'))
      return
    }
    if (!groupSegment?.segmentationId) {
      setError(new Error('Selected segment has no segmentation'))
      return
    }
    const audience = values.audience === '' ? null : Number(values.audience)
    const metricDays = values.metricDays === '' ? null : Number(values.metricDays)
    const targetUserCount = values.targetUserCount === '' ? null : Number(values.targetUserCount)
    if (audience !== null && (!Number.isInteger(audience) || audience < 0 || audience > 100)) {
      setError(new Error('Audience must be an integer between 0 and 100'))
      return
    }
    if (metricDays !== null && (!Number.isInteger(metricDays) || metricDays < 0)) {
      setError(new Error('Metric days must be a non-negative integer'))
      return
    }
    if (targetUserCount !== null && (!Number.isInteger(targetUserCount) || targetUserCount < 0)) {
      setError(new Error('Target user count must be a non-negative integer'))
      return
    }
    if (metricDays !== null && !String(values.targetMetric || '').trim()) {
      setError(new Error('Target metric is required when metric days is set'))
      return
    }
    if (String(values.targetMetric || '').trim() && metricDays === null) {
      setError(new Error('Metric days is required when target metric is set'))
      return
    }
    if (values.automaticDistributionStop && !values.testFinishDate) {
      setError(new Error('Test finish date is required when automatic stop is enabled'))
      return
    }

    const variantsPayload = variants.map((v) => ({
      name: String(v.name || '').trim(),
      trafficPercent: Number(v.trafficPercent ?? 0),
      isDefault: Boolean(v.isDefault),
      elements: v.elementId && Number(v.elementId) > 0 ? [{ elementId: Number(v.elementId), condition: null }] : [],
    }))
    const totalNonDefault = variantsPayload
      .filter((v) => !v.isDefault)
      .reduce((s, v) => s + v.trafficPercent, 0)
    if (totalNonDefault !== 100) {
      setError(new Error('Traffic percents for test variants must sum to 100%'))
      return
    }

    try {
      const created = await createAbTestRequest(applicationId, {
        groupConfigId: values.groupConfigId,
        groupSegmentId: values.groupSegmentId || (groupSegment?.id ?? null),
        name,
        audience,
        targetMetric: values.targetMetric,
        metricDays,
        targetUserCount,
        isOnlyNewUser: values.isOnlyNewUser,
        automaticDistributionStop: values.automaticDistributionStop,
        releaseAllVersions: values.releaseAllVersions,
        testFinishDate: values.testFinishDate ? new Date(values.testFinishDate).toISOString() : null,
        variants: variantsPayload,
      })
      if (!created) throw new Error('AB test was not created')
      storeActions.hydrateAbTests(applicationId, [created])
      navigate(`/applications/${applicationId}/ab-tests/${created.id}`)
    } catch (err) {
      setError(err)
    }
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create AB test</h1>
        <Link to={`/applications/${applicationId}/ab-tests`} className="button buttonGhost">
          Back to AB tests
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">AB test</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">Group config</span>
            <select className="textInput" value={values.groupConfigId} onChange={onChange('groupConfigId')}>
              <option value="">Select group config…</option>
              {groupConfigs.map((gc) => (
                <option key={gc.id} value={gc.id}>
                  {gc.name || gc.id}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Segment (from latest draft)</span>
            <select className="textInput" value={values.groupSegmentId} onChange={onChange('groupSegmentId')}>
              <option value="">Select segment…</option>
              {availableSegments.map((gs) => {
                const seg = segmentationsById[gs.segmentationId]
                return (
                  <option key={gs.id} value={gs.id}>
                    {gs.priority}. {seg?.name || 'Unnamed'} {gs.isDefault ? '(default)' : ''}
                  </option>
                )
              })}
            </select>
            <span className="muted mutedMeta">
              AB test will be attached to the selected segment and will continue after releases.
            </span>
          </label>

          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} placeholder="e.g. prices_test" />
          </label>

          <label className="formField">
            <span className="formLabel">Audience (%)</span>
            <input
              className="textInput"
              type="number"
              min="0"
              max="100"
              value={values.audience}
              onChange={onChange('audience')}
              placeholder="0-100"
            />
          </label>

          <label className="formField">
            <span className="formLabel">Target metric</span>
            <select className="textInput" value={values.targetMetric} onChange={onChange('targetMetric')}>
              <option value="">Select target metric…</option>
              {TARGET_METRIC_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Metric/Day:</span>
            <select className="textInput" value={values.metricDays} onChange={onChange('metricDays')}>
              <option value="">Select…</option>
              {METRIC_DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Target user count</span>
            <input
              className="textInput"
              type="number"
              min="0"
              value={values.targetUserCount}
              onChange={onChange('targetUserCount')}
              placeholder="e.g. 10000"
            />
          </label>

          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Test only on new users
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.isOnlyNewUser}
              onChange={onChange('isOnlyNewUser')}
            />
          </label>

          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Automatic distribution stop
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.automaticDistributionStop}
              onChange={onChange('automaticDistributionStop')}
            />
          </label>

          {values.automaticDistributionStop ? (
            <label className="formField">
              <span className="formLabel">Test finish date (UTC)</span>
              <input
                className="textInput"
                type="datetime-local"
                value={values.testFinishDate}
                onChange={onChange('testFinishDate')}
              />
            </label>
          ) : null}

          <label className="formField formSwitch">
            <span className="formLabel formLabelNoMargin">
              Release all versions
            </span>
            <input
              type="checkbox"
              className="formCheckInput"
              checked={values.releaseAllVersions}
              onChange={onChange('releaseAllVersions')}
            />
          </label>

          <div className="formField formFieldFullWidth">
            <span className="formLabel">Variants</span>
            <p className="muted mutedMetaHint">
              Default variant has 0% traffic. Other variants must sum to 100%.
            </p>
            {variants.map((variant, vIdx) => (
              <div
                key={`${variant.name}-${vIdx}`}
                className={`variantCard${vIdx > 0 ? ' variantCardSpaced' : ''}`}
              >
                <div className="builderInlineRow builderInlineRowMb8 builderInlineRowWrap">
                  <input
                    className="textInput inputW100"
                    value={variant.name}
                    onChange={(e) => updateVariant(vIdx, { name: e.target.value })}
                    placeholder="Name"
                    readOnly={variant.isDefault}
                  />
                  <input
                    className="textInput inputW70"
                    type="number"
                    min="0"
                    max="100"
                    value={variant.trafficPercent}
                    onChange={(e) =>
                      updateVariant(vIdx, { trafficPercent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })
                    }
                    readOnly={variant.isDefault}
                  />
                  <span className="muted">%</span>
                  {!variant.isDefault && variants.filter((v) => !v.isDefault).length > 1 && (
                    <button
                      type="button"
                      className="linkButton"
                      onClick={() => removeVariant(vIdx)}
                    >
                      Remove variant
                    </button>
                  )}
                </div>
                <div className="builderInlineRow builderInlineRowMt6 builderInlineRowWrap">
                  <select
                    className="textInput inputMinW200"
                    value={variant.elementId || ''}
                    onChange={(e) => updateVariantElement(vIdx, e.target.value)}
                  >
                    <option value="">Select element…</option>
                    {publishedElements.map((el) => (
                      <option key={el.id} value={el.id}>
                        {el.name || el.id} ({el.elementType}, v{el.version})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="button buttonGhost buttonTop12"
              onClick={addVariant}
            >
              Add
            </button>
          </div>

          <div className="formActions">
            <button type="submit" className="button buttonPrimary">
              Create
            </button>
          </div>
        </form>

        {error ? <div className="formError">{String(error?.message || error)}</div> : null}
      </section>
    </div>
  )
}

